/**
 * Cliente HTTP para assinaturas anexadas ao caso (EW.F2.T06). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/types/case_signatures.py e apps/api/runtime/case_signatures.py. // ux-neutrality:allow doc-comment
 * Cobre: // ux-neutrality:allow doc-comment
 * - uploadSignature: POST multipart /casos/:case_id/signatures com onProgress. // ux-neutrality:allow doc-comment
 * - listSignatures: GET /casos/:case_id/signatures. // ux-neutrality:allow doc-comment
 * - deleteSignature: DELETE /casos/:case_id/signatures/:signature_id. // ux-neutrality:allow doc-comment
 * - getSignatureBlobUrl: helper de path para fetch autenticado de imagem. // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { nomeDoAnexo, type ArquivoBaixado } from './http_download';

// Contrato ----------------------------------------------------------------

export type SignatureKind = 'standard' | 'contested';

export type ExtractionSource = 'direct_upload' | 'llm_extracted' | 'manual_crop';

export interface SignatureRecordData {
  id: string;
  case_id: string;
  usuario_id: string;
  kind: SignatureKind;
  blob_path: string;
  label: string | null;
  especime_date: string | null;
  mime_type: string;
  size_bytes: number;
  source_document_id?: string | null;
  extraction_source?: ExtractionSource;
  /** Nome do arquivo de origem (E23.F1) — null para direct_upload. */
  source_document_filename?: string | null;
  /** Página do candidate aceito (E23.F1) — null quando indisponível. */
  source_document_page?: number | null;
  created_at: string;
  deleted_at?: string | null;
  /** E71.F1 (ADR-0073): orientação aplicada ao recorte (0/90/180/270). */
  rotation_deg?: 0 | 90 | 180 | 270;
}

export interface SignatureUploadPayload {
  file: File;
  kind: SignatureKind;
  label?: string | null;
  especime_date?: string | null;
  onProgress?: (percent: number) => void;
}

// Erros tipados -----------------------------------------------------------

export type SignatureErrorReason =
  | 'case_not_found'
  | 'signature_not_found'
  | 'image_invalid'
  | 'image_too_large'
  | 'validation'
  | 'network'
  | 'unknown';

export class SignatureError extends Error {
  public readonly reason: SignatureErrorReason;

  constructor(reason: SignatureErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'SignatureError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_UNPROCESSABLE = 422;

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

export function classifySignatureError(err: unknown): SignatureError {
  if (!axios.isAxiosError(err)) return new SignatureError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status;
  const detail = detailString(axiosErr);

  if (httpStatus === HTTP_NOT_FOUND) {
    if (detail === 'CASE_NOT_FOUND') return new SignatureError('case_not_found');
    return new SignatureError('signature_not_found');
  }
  if (httpStatus === HTTP_PAYLOAD_TOO_LARGE) {
    return new SignatureError('image_too_large');
  }
  if (httpStatus === HTTP_UNPROCESSABLE) {
    if (detail.startsWith('SIGNATURE_IMAGE_')) {
      return new SignatureError('image_invalid');
    }
    return new SignatureError('validation');
  }
  if (isNetworkError(axiosErr)) return new SignatureError('network');
  return new SignatureError('unknown');
}

// HTTP --------------------------------------------------------------------

export async function uploadSignature(
  client: AxiosInstance,
  caseId: string,
  payload: SignatureUploadPayload,
): Promise<SignatureRecordData> {
  const form = new FormData();
  form.append('file', payload.file);
  form.append('kind', payload.kind);
  if (payload.label != null && payload.label !== '') {
    form.append('label', payload.label);
  }
  if (payload.especime_date != null && payload.especime_date !== '') {
    form.append('especime_date', payload.especime_date);
  }
  try {
    const { data } = await client.post<SignatureRecordData>(`/casos/${caseId}/signatures`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (payload.onProgress && evt.total != null && evt.total > 0) {
          payload.onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    });
    return data;
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export async function listSignatures(
  client: AxiosInstance,
  caseId: string,
): Promise<SignatureRecordData[]> {
  try {
    const { data } = await client.get<SignatureRecordData[]>(`/casos/${caseId}/signatures`);
    return data;
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export async function deleteSignature(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
): Promise<void> {
  try {
    await client.delete(`/casos/${caseId}/signatures/${signatureId}`);
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export interface RecropPayload {
  bbox: { x: number; y: number; w: number; h: number };
  page: number;
  /** E71.F1: omitido = o servidor reaplica a orientação persistida. */
  rotation_deg?: 0 | 90 | 180 | 270;
}

export async function recropSignature(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
  payload: RecropPayload,
): Promise<SignatureRecordData> {
  try {
    const { data } = await client.patch<SignatureRecordData>(
      `/casos/${caseId}/signatures/${signatureId}/bbox`,
      payload,
    );
    return data;
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export async function patchSignatureLabel(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
  label: string,
): Promise<SignatureRecordData> {
  try {
    const { data } = await client.patch<SignatureRecordData>(
      `/casos/${caseId}/signatures/${signatureId}`,
      { label },
    );
    return data;
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export function getSignatureBlobUrl(caseId: string, signatureId: string): string {
  return `/casos/${caseId}/signatures/${signatureId}/blob`;
}

/**
 * Baixa o RECORTE da assinatura para guardar (E65.F6.C02 — F-E65.F6-02).
 *
 * Rota diferente do `/blob` de propósito: aquela serve a imagem para a tela
 * DESENHAR e é chamada em toda renderização; esta é exportação, e é a única que
 * o servidor registra na trilha. Marcar renderização como exportação faria a
 * trilha mentir por excesso.
 */
export async function baixarArquivoDaAssinatura(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
  nomeSugerido: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/signatures/${signatureId}/arquivo`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], nomeSugerido),
    };
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export async function fetchSignatureBlob(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(getSignatureBlobUrl(caseId, signatureId), {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifySignatureError(err);
  }
}

export function getSignatureTopographyUrl(caseId: string, signatureId: string): string {
  return `/casos/${caseId}/signatures/${signatureId}/topography`;
}

// Gênese gráfica (E26.F2/ADR-0037) — espelha types/forensic.py::GraphicGenesis.

export interface GenesisPointData {
  x: number;
  y: number;
}

export interface GraphicGenesisData {
  image_width: number;
  image_height: number;
  /** Extremidades de traço — SEM direção temporal (ADR-0037 §A). */
  extremities: GenesisPointData[];
  junctions: GenesisPointData[];
  /** Paradas ESTIMADAS (centróides de acúmulo de tinta). */
  estimated_stops: GenesisPointData[];
}

// Matriz de valores físicos (E26.F3/ADR-0038) — espelha
// types/forensic.py::SignaturePhysicalValues.

export type PhysicalValueUnit = 'px' | 'graus' | 'razao' | 'contagem' | 'indice' | 'coeficiente';

export interface PhysicalValueData {
  key: string;
  criterion_id: number;
  /** Rótulo pericial PT-BR vindo do backend (fonte única com o laudo). */
  label: string;
  value: number;
  unit: PhysicalValueUnit;
  /** Forma na unidade canônica ×espessura-do-traço (ADR-0013); null quando
   * não-px ou espessura não resolvida. */
  normalized_by_stroke: number | null;
}

export interface SignaturePhysicalValuesData {
  stroke_width_px: number | null;
  values: PhysicalValueData[];
}

export function getSignaturePhysicalValuesUrl(caseId: string, signatureId: string): string {
  return `/casos/${caseId}/signatures/${signatureId}/physical-values`;
}

/**
 * Busca a matriz de valores físicos (E26.F3/ADR-0038).
 *
 * Devolve `null` quando o backend responde 422 (`PHYSICAL_VALUES_UNAVAILABLE`):
 * a imagem não sustenta a extração — estado "indisponível", não erro.
 */
export async function fetchSignaturePhysicalValues(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
): Promise<SignaturePhysicalValuesData | null> {
  try {
    const { data } = await client.get<SignaturePhysicalValuesData>(
      getSignaturePhysicalValuesUrl(caseId, signatureId),
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === HTTP_UNPROCESSABLE) {
      return null;
    }
    throw classifySignatureError(err);
  }
}

export function getSignatureGraphicGenesisUrl(caseId: string, signatureId: string): string {
  return `/casos/${caseId}/signatures/${signatureId}/graphic-genesis`;
}

/**
 * Busca o payload da gênese gráfica (E26.F2/ADR-0037).
 *
 * Devolve `null` quando o backend responde 422 (`GRAPHIC_GENESIS_UNAVAILABLE`):
 * a imagem não sustenta marcadores honestos — estado "indisponível", não erro.
 */
export async function fetchSignatureGraphicGenesis(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
): Promise<GraphicGenesisData | null> {
  try {
    const { data } = await client.get<GraphicGenesisData>(
      getSignatureGraphicGenesisUrl(caseId, signatureId),
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === HTTP_UNPROCESSABLE) {
      return null;
    }
    throw classifySignatureError(err);
  }
}

/**
 * Busca o PNG derivado da topografia de pressão estimada (E26.F1/ADR-0036).
 *
 * Devolve `null` quando o backend responde 422 (`TOPOGRAPHY_UNAVAILABLE`):
 * a imagem não sustenta uma topografia honesta e a UI exibe o estado
 * "indisponível" — não é erro. O status é checado direto (sem ler `detail`)
 * porque com `responseType: 'blob'` o corpo do erro chega como Blob
 * (gotcha E23.F20).
 */
export async function fetchSignatureTopography(
  client: AxiosInstance,
  caseId: string,
  signatureId: string,
): Promise<Blob | null> {
  try {
    const { data } = await client.get<Blob>(getSignatureTopographyUrl(caseId, signatureId), {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === HTTP_UNPROCESSABLE) {
      return null;
    }
    throw classifySignatureError(err);
  }
}
